class BraceletStackBuilder extends HTMLElement {
  constructor() {
    super();
    this.maxSelections = 6;
    this.selected = [];
    this.optionButtons = [];
    this.selectionCounter = 0;
  }

  connectedCallback() {
    this.optionButtons = Array.from(this.querySelectorAll('[data-bracelet-option]'));
    this.maxSelections = Number(this.dataset.maxSelections) || this.maxSelections;
    this.countEl = this.querySelector('[data-selected-count]');
    this.statusEl = this.querySelector('[data-builder-status]');
    this.addButton = this.querySelector('[data-add-to-cart]');
    this.baseButtonLabel = this.addButton ? this.addButton.textContent.trim() : 'Add stack to cart';
    this.priceEl = this.querySelector('[data-stack-price]');
    this.selectedList = this.querySelector('[data-selected-list]');
    this.slots = Array.from(this.querySelectorAll('[data-stack-slot]'));
    this.priceTiers = {
      1: 18,
      2: 28,
      3: 35,
      4: 40,
      5: 45,
      6: 48,
    };

    this.optionButtons.forEach((button) => {
      button.addEventListener('click', () => this.toggleBracelet(button));
    });

    if (this.selectedList) {
      this.selectedList.addEventListener('change', (event) => {
        if (event.target.matches('[data-bracelet-select]')) {
          this.selectBraceletFromMenu(event.target.value);
        }
      });
    }

    if (this.addButton) {
      this.addButton.addEventListener('click', () => this.addToCart());
    }

    this.render();
  }

  toggleBracelet(button) {
    this.cartErrorMessage = '';
    const bracelet = this.getBraceletFromButton(button);

    if (this.selected.length >= this.maxSelections) {
      return;
    }

    if (!bracelet.variantId || bracelet.available !== 'true') {
      return;
    }

    this.selected.push(this.createSelection(bracelet));
    this.render();
  }

  getBraceletFromButton(button) {
    return {
      id: button.dataset.id,
      variantId: button.dataset.variantId,
      title: button.dataset.title,
      image: button.dataset.image,
      productUrl: button.dataset.productUrl,
      available: button.dataset.available,
    };
  }

  createSelection(bracelet) {
    this.selectionCounter += 1;
    return Object.assign({}, bracelet, {
      selectionKey: `${bracelet.id}-${this.selectionCounter}`,
    });
  }

  getBraceletById(id) {
    const button = this.optionButtons.find((optionButton) => optionButton.dataset.id === id);
    return button ? this.getBraceletFromButton(button) : null;
  }

  selectBraceletFromMenu(id) {
    if (!id) return;
    this.cartErrorMessage = '';

    const bracelet = this.getBraceletById(id);
    if (!bracelet) return;

    if (this.selected.length >= this.maxSelections) {
      this.render();
      return;
    }

    if (!bracelet.variantId || bracelet.available !== 'true') {
      this.render();
      return;
    }

    this.selected.push(this.createSelection(bracelet));
    this.render();
  }

  moveBracelet(selectionKey, direction) {
    this.cartErrorMessage = '';
    const index = this.selected.findIndex((item) => item.selectionKey === selectionKey);
    const nextIndex = index + direction;

    if (index < 0 || nextIndex < 0 || nextIndex >= this.selected.length) return;

    const [item] = this.selected.splice(index, 1);
    this.selected.splice(nextIndex, 0, item);
    this.render();
  }

  removeBracelet(selectionKey) {
    this.cartErrorMessage = '';
    this.selected = this.selected.filter((item) => item.selectionKey !== selectionKey);
    this.render();
  }

  render() {
    this.renderCount();
    this.renderOptions();
    this.renderPreview();
    this.renderSelectedList();
    this.renderPrice();

    if (this.addButton) {
      this.addButton.disabled = this.selected.length === 0 || this.selected.length > this.maxSelections || this.addButton.hasAttribute('aria-busy');
    }
  }

  renderCount() {
    if (this.countEl) {
      this.countEl.textContent = `${this.selected.length}/${this.maxSelections}`;
    }
  }

  renderPrice() {
    const price = this.getStackPrice();
    const formattedPrice = price ? this.formatPrice(price) : 'Choose bracelets';

    if (this.priceEl) {
      this.priceEl.textContent = formattedPrice;
    }

    if (this.addButton && this.cartErrorMessage && !this.addButton.hasAttribute('aria-busy')) {
      this.addButton.textContent = this.cartErrorMessage;
      return;
    }

    if (this.addButton && !this.addButton.hasAttribute('aria-busy')) {
      this.addButton.textContent = price ? `${this.baseButtonLabel} - ${formattedPrice}` : this.baseButtonLabel;
    }
  }

  getStackPrice() {
    return this.priceTiers[this.selected.length] || 0;
  }

  getNextBraceletPrice() {
    if (this.selected.length >= this.maxSelections) return null;

    const currentPrice = this.priceTiers[this.selected.length] || 0;
    const nextPrice = this.priceTiers[this.selected.length + 1] || currentPrice;

    return nextPrice - currentPrice;
  }

  formatPrice(price) {
    return `£${price}`;
  }

  renderOptions() {
    const nextBraceletPrice = this.getNextBraceletPrice();

    this.optionButtons.forEach((button) => {
      const isSelected = this.selected.some((item) => item.id === button.dataset.id);
      const state = button.querySelector('[data-option-state]');
      const price = button.querySelector('[data-option-price]');

      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-pressed', String(isSelected));

      if (state) {
        state.textContent = isSelected ? 'Add another' : 'Select';
      }

      if (price) {
        price.textContent = nextBraceletPrice === null ? 'Stack complete' : this.formatPrice(nextBraceletPrice);
      }
    });
  }

  renderPreview() {
    this.slots.forEach((slot, index) => {
      const bracelet = this.selected[index];
      slot.innerHTML = '';

      if (!bracelet) {
        return;
      }

      if (bracelet.image) {
        const image = document.createElement('img');
        image.className = 'bracelet-stack-builder__preview-bracelet';
        image.src = bracelet.image;
        image.alt = bracelet.title;
        slot.append(image);
        return;
      }

      const placeholder = document.createElement('span');
      placeholder.className = 'bracelet-stack-builder__preview-placeholder';
      placeholder.setAttribute('aria-label', bracelet.title);
      slot.append(placeholder);
    });
  }

  renderSelectedList() {
    if (!this.selectedList) return;

    this.selectedList.innerHTML = '';

    for (let index = 0; index < this.maxSelections; index += 1) {
      const bracelet = this.selected[index];
      const item = document.createElement('li');

      if (!bracelet) {
        if (index > this.selected.length) continue;

        item.className = 'bracelet-stack-builder__selected-empty';
        item.innerHTML = `<span>Bracelet ${index + 1}</span>${this.renderBraceletSelect(index)}`;
        this.selectedList.append(item);
        break;
      }

      item.className = 'bracelet-stack-builder__selected-item';

      const copy = document.createElement('span');
      copy.innerHTML = `<span class="bracelet-stack-builder__selected-title">${this.escapeHtml(bracelet.title)}</span><span class="bracelet-stack-builder__selected-position">Bracelet ${index + 1}</span>`;

      const actions = document.createElement('span');
      actions.className = 'bracelet-stack-builder__selected-actions';
      actions.append(
        this.createIconButton('Move up', '↑', () => this.moveBracelet(bracelet.selectionKey, -1), index === 0),
        this.createIconButton('Move down', '↓', () => this.moveBracelet(bracelet.selectionKey, 1), index === this.selected.length - 1),
        this.createIconButton('Remove', '×', () => this.removeBracelet(bracelet.selectionKey), false)
      );

      item.append(copy, actions);
      this.selectedList.append(item);
    }
  }

  createIconButton(label, symbol, onClick, disabled) {
    const button = document.createElement('button');
    button.className = 'bracelet-stack-builder__icon-button';
    button.type = 'button';
    button.title = label;
    button.setAttribute('aria-label', label);
    button.textContent = symbol;
    button.disabled = disabled;
    button.addEventListener('click', onClick);
    return button;
  }

  renderBraceletSelect(index) {
    const options = this.optionButtons
      .filter((button) => button.dataset.available === 'true')
      .map((button) => `<option value="${this.escapeAttribute(button.dataset.id)}">${this.escapeHtml(button.dataset.title)}</option>`)
      .join('');

    return `
      <select class="bracelet-stack-builder__selection-select" aria-label="Choose bracelet ${index + 1}" data-bracelet-select>
        <option value="">Choose a bracelet</option>
        ${options}
      </select>
    `;
  }

  async addToCart() {
    if (this.selected.length === 0 || this.selected.length > this.maxSelections || !this.addButton) return;
    this.cartErrorMessage = '';

    const properties = this.selected.reduce((stackProperties, bracelet, index) => {
      stackProperties[`Bracelet ${index + 1}`] = bracelet.title;
      if (bracelet.image) {
        stackProperties[`_Bracelet ${index + 1} image`] = bracelet.image;
      }
      return stackProperties;
    }, {
      'Stack Price': this.formatPrice(this.getStackPrice()),
    });

    const stackVariantId = this.getAttribute(`data-stack-variant-${this.selected.length}`);
    const items = stackVariantId
      ? [
          {
            id: Number(stackVariantId),
            quantity: 1,
            properties,
          },
        ]
      : this.selected.map((bracelet) => ({
          id: Number(bracelet.variantId),
          quantity: 1,
          properties,
        }));

    this.addButton.disabled = true;
    this.addButton.setAttribute('aria-busy', 'true');
    this.addButton.textContent = 'Adding your stack...';

    try {
      const response = await fetch(this.dataset.cartUrl || '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || 'Unable to add this stack to cart.');
      }

      if (this.dataset.successAction === 'cart') {
        window.location.href = this.dataset.cartPageUrl || '/cart';
      } else {
        this.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
    } catch (error) {
      this.cartErrorMessage = error.message;
    } finally {
      this.addButton.removeAttribute('aria-busy');
      this.render();
    }
  }

  setStatus() {
    if (!this.statusEl) return;

    this.statusEl.textContent = 'choose up to 6 bracelets';
    this.statusEl.classList.remove('is-error');
  }

  escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  }

  escapeAttribute(value = '') {
    return this.escapeHtml(value).replace(/"/g, '&quot;');
  }

  ordinalWord(number) {
    return ['first', 'second', 'third', 'fourth', 'fifth', 'sixth'][number - 1] || `${number}th`;
  }
}

if (!customElements.get('bracelet-stack-builder')) {
  customElements.define('bracelet-stack-builder', BraceletStackBuilder);
}
